// KARIS Module Configurations
// field types: text (default), number, date, options[], ref ("endpoint" or {endpoint, labelKey, valueKey})
// approval_required flag is informational (backend enforces)
import { printInvoicePdf, printWorkOrderCompletion } from "@/lib/printDoc";

const STATUS_OPTIONS = {
  invoice: ["unpaid_invoice", "partially_paid", "paid", "overdue", "cancelled"],
  generic: ["draft", "active", "pending", "approved", "rejected", "cancelled"],
  ticket: ["open", "in-progress", "resolved", "closed"],
  priority: ["low", "medium", "high", "critical"],
  workorder: ["scheduled", "in-progress", "completed", "cancelled"],
  pr: ["pending", "approved", "rejected"],
  po: ["pending", "approved", "received", "cancelled"],
  contract: ["active", "expired", "cancelled"],
  asset: ["in-use", "idle", "disposed", "maintenance"],
  emp: ["active", "on-leave", "resigned"],
  leave: ["pending", "approved", "rejected"],
  payroll: ["draft", "paid"],
};

export const MODULES = {
  // ============= CRM =============
  "crm-leads": {
    title: "Leads", subtitle: "Track and qualify potential customers", endpoint: "/leads",
    fields: [
      { name: "name", label: "Lead / Company", required: true },
      { name: "contact", label: "Contact Person" },
      { name: "phone", label: "Phone" },
      { name: "source", label: "Source", options: ["Referral", "Website", "Cold Call", "Walk-in", "Email", "Event"] },
      { name: "status", label: "Status", options: ["new", "contacted", "qualified", "lost"] },
      { name: "value", label: "Estimated Value", type: "number" },
    ],
    columns: ["name", "contact", "phone", "source", "status", "value"],
    statusOptions: ["new", "contacted", "qualified", "lost"],
  },
  "crm-customers": {
    title: "Customers", subtitle: "Active client accounts", endpoint: "/customers",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "company", label: "Company" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Phone" },
      { name: "city", label: "City" },
      { name: "status", label: "Status", options: ["active", "inactive", "lead"] },
      { name: "credit_limit", label: "Credit Limit", type: "number" },
    ],
    columns: ["name", "company", "email", "phone", "city", "status", "credit_limit"],
    statusOptions: ["active", "inactive", "lead"],
  },
  "crm-invoices": {
    title: "Invoices", subtitle: "Customer billing and receivables", endpoint: "/invoices",
    lineItems: true, vatToggle: true, recordPayment: true,
    // Bill in TZS or USD; amount/tax always stay TZS (see LineItemsEditor).
    currency: true,
    customPrint: printInvoicePdf,
    // Powers the "Create Work Order" button on an invoice's detail view --
    // prefills a new work order from this invoice instead of starting blank.
    workOrderPrefill: (record) => ({
      customer: record.customer || "",
      installation_site: record.installation_site || "",
      invoice_no: record.invoice_no || "",
      title: record.invoice_title || `Install for invoice ${record.invoice_no || ""}`,
    }),
    fields: [
      // Invoice No. is always backend-generated (shared sequence starting at
      // 6010, suffixed by Invoice Type, e.g. 6010/C, 6011/S, 6012/IT -- see
      // backend/doc_numbering.py) -- never typed by hand.
      { name: "invoice_no", label: "Invoice No.", required: true, readOnly: true },
      // Proforma vs Tax Invoice is a separate axis from Invoice Type below --
      // it controls the PDF title, and proforma invoices are excluded from
      // revenue/receivables reporting since they aren't a real billing
      // commitment yet. Both share one number pool, so converting a proforma
      // keeps its number.
      { name: "doc_type", label: "Document Type", required: true, options: ["Tax Invoice", "Proforma Invoice"] },
      { name: "invoice_type", label: "Invoice Type", required: true, options: [
        "CCTV Project", "Internet Subscription", "Product Supply", "IT Support", "Other Services",
      ]},
      // A short human-readable summary of what the invoice is for (e.g.
      // "CCTV Installation - Kinyasini") -- shown in Reports/Customer Ledger
      // and printed statements so a list of invoice numbers is legible
      // without opening each one.
      { name: "invoice_title", label: "Invoice Title", placeholder: "e.g. CCTV Installation - Kinyasini" },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "installation_site", label: "Installation Site" },
      { name: "issue_date", label: "Issue Date", type: "date" },
      { name: "due_date", label: "Expiry Date", type: "date" },
      // Amount paid / currency are recorded against the Sale, not the
      // invoice itself. Status is still computed (unpaid -> partially paid
      // -> paid) from amount_paid on the record -- see backend/crud_factory.py.
    ],
    columns: ["invoice_no", "doc_type", "invoice_title", "customer", "total", "total_usd", "amount_paid", "balance", "status"],
    // Total is amount + tax (tax is 0 when VAT is off), matching how the
    // backend derives invoice status. Each returns null when an input was
    // stripped by hidden_fields, so these columns can't reveal hidden values.
    computedColumns: {
      total: (r) => (r.amount == null || r.tax == null ? null : r.amount + r.tax),
      // Only USD invoices have one; "—" for TZS invoices.
      total_usd: (r) => (r.currency !== "USD" || r.amount_fc == null ? null : r.amount_fc + (r.tax_fc || 0)),
      balance: (r) => (r.amount == null || r.tax == null || r.amount_paid == null
        ? null : r.amount + r.tax - r.amount_paid),
    },
    statusOptions: STATUS_OPTIONS.invoice,
  },
  "crm-sales-orders": {
    title: "Sales", subtitle: "Completed sales — products or an existing invoice", endpoint: "/sales-orders",
    lineItems: true, linkableInvoice: true,
    fields: [
      { name: "order_no", label: "Sale No.", required: true, readOnly: true },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "payment_status", label: "Payment Status", options: ["full", "partial"] },
      { name: "due_date", label: "Due Date", type: "date", showIf: (f) => f.payment_status === "partial" },
    ],
    columns: ["order_no", "customer", "amount", "payment_status", "due_date"],
    statusOptions: [],
    convertTo: [{ to: "Invoice", endpoint: "/invoices/", path: "/crm/invoices" }],
  },
  "crm-contracts": {
    title: "Service Contracts", subtitle: "Service & maintenance contracts", endpoint: "/service-contracts",
    fields: [
      { name: "contract_no", label: "Contract No.", required: true },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "type", label: "Type", options: ["Internet", "CCTV", "Networking", "Fiber", "Maintenance", "Internet + CCTV", "Full Stack"] },
      { name: "value", label: "Value", type: "number" },
      { name: "start_date", label: "Start", type: "date" },
      { name: "end_date", label: "End", type: "date" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.contract },
    ],
    columns: ["contract_no", "customer", "type", "value", "start_date", "end_date", "status"],
    statusOptions: STATUS_OPTIONS.contract,
  },

  // ============= Purchases =============
  "pur-suppliers": {
    title: "Suppliers", subtitle: "Vendors and procurement partners", endpoint: "/suppliers",
    fields: [
      { name: "name", label: "Supplier Name", required: true },
      { name: "email", label: "Email" },
      { name: "phone", label: "Phone" },
      { name: "country", label: "Country", options: ["Tanzania", "Kenya", "Uganda", "South Africa", "UAE", "China", "USA"] },
      { name: "category", label: "Category", options: ["Networking", "CCTV", "Fiber", "Hardware", "Software", "Services"] },
    ],
    columns: ["name", "email", "phone", "country", "category"],
  },
  "pur-requisitions": {
    title: "Purchase Requisitions", subtitle: "Internal procurement requests", endpoint: "/purchase-requisitions",
    fields: [
      { name: "pr_no", label: "PR No.", required: true },
      { name: "requester", label: "Requester", ref: "/employees", refLabel: "name" },
      { name: "items", label: "Items" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.pr },
    ],
    columns: ["pr_no", "requester", "items", "amount", "status"],
    statusOptions: STATUS_OPTIONS.pr,
  },
  "pur-orders": {
    title: "Purchase Orders", subtitle: "Requires approval before issuing", endpoint: "/purchase-orders",
    approval_required: true,
    fields: [
      { name: "po_no", label: "PO No.", required: true },
      { name: "supplier", label: "Supplier", ref: "/suppliers", refLabel: "name" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.po },
    ],
    columns: ["po_no", "supplier", "amount", "status"],
    statusOptions: STATUS_OPTIONS.po,
  },
  "pur-supplier-invoices": {
    title: "Supplier Invoices", subtitle: "Bills from suppliers (payables)", endpoint: "/supplier-invoices",
    fields: [
      { name: "invoice_no", label: "Invoice No.", required: true },
      { name: "supplier", label: "Supplier", ref: "/suppliers", refLabel: "name" },
      { name: "amount", label: "Amount", type: "number" },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.invoice },
    ],
    columns: ["invoice_no", "supplier", "amount", "due_date", "status"],
    statusOptions: STATUS_OPTIONS.invoice,
  },
  "pur-grn": {
    title: "Goods Received Notes", subtitle: "Inbound delivery records", endpoint: "/goods-received",
    fields: [
      { name: "grn_no", label: "GRN No.", required: true },
      { name: "supplier", label: "Supplier", ref: "/suppliers", refLabel: "name" },
      { name: "po_ref", label: "PO Ref" },
      { name: "received_date", label: "Received Date", type: "date" },
      { name: "status", label: "Status", options: ["received", "partial", "rejected"] },
    ],
    columns: ["grn_no", "supplier", "po_ref", "received_date", "status"],
    statusOptions: ["received", "partial", "rejected"],
  },

  // ============= Accounting =============
  "acc-chart": {
    title: "Chart of Accounts", subtitle: "Account codes and balances", endpoint: "/chart-of-accounts",
    // Clicking a row goes straight to that account's statement (revenue in
    // / expenses out) instead of the generic read-only detail dialog.
    rowLink: (it) => `/accounting/statement/${encodeURIComponent(it.name)}`,
    fields: [
      { name: "code", label: "Code", required: true },
      { name: "name", label: "Account Name", required: true },
      { name: "type", label: "Type", options: ["Asset", "Liability", "Equity", "Income", "Expense"] },
      { name: "balance", label: "Balance", type: "number" },
    ],
    columns: ["code", "name", "type", "balance"],
  },
  "acc-journals": {
    title: "Journal Entries", subtitle: "Double-entry bookkeeping", endpoint: "/journal-entries",
    fields: [
      { name: "entry_no", label: "Entry No.", required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "description", label: "Description" },
      { name: "debit_account", label: "Debit Account", ref: "/chart-of-accounts", refLabel: "name" },
      { name: "credit_account", label: "Credit Account", ref: "/chart-of-accounts", refLabel: "name" },
      { name: "amount", label: "Amount", type: "number" },
    ],
    columns: ["entry_no", "date", "description", "debit_account", "credit_account", "amount"],
  },
  "acc-expenses": {
    title: "Expenses", subtitle: "Operating expenses (approval required)", endpoint: "/expenses",
    approval_required: true,
    fields: [
      { name: "category", label: "Category", required: true, options: [
        "Transportation", "Vehicle Maintenance", "Fuel", "Installation Materials", "Site Operations",
        "Salaries & Staff Costs", "Staff Welfare", "Utilities", "Rent", "Communication",
        "Bandwidth & Systems", "Bank & Mobile Charges", "Office & Admin", "Tax & Statutory",
        "Refunds", "Marketing", "Miscellaneous",
      ]},
      { name: "description", label: "Description" },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "paid_by", label: "Paid By", ref: "/chart-of-accounts", refLabel: "name", searchable: true },
    ],
    columns: ["category", "description", "amount", "date", "paid_by", "status"],
    statusOptions: ["pending_approval", "approved", "rejected"],
  },
  "acc-deposits": {
    title: "Deposits", subtitle: "Bank and cash deposits", endpoint: "/deposits",
    fields: [
      { name: "reference", label: "Reference" },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "bank", label: "Account", ref: "/chart-of-accounts", refLabel: "name", searchable: true },
      { name: "date", label: "Date", type: "date" },
    ],
    columns: ["reference", "amount", "bank", "date"],
  },
  "acc-transfers": {
    title: "Fund Transfers", subtitle: "Inter-account transfers", endpoint: "/fund-transfers",
    fields: [
      { name: "from_account", label: "From Account", ref: "/chart-of-accounts", refLabel: "name", searchable: true, required: true },
      { name: "to_account", label: "To Account", ref: "/chart-of-accounts", refLabel: "name", searchable: true, required: true },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "reason", label: "Reason", required: true },
      { name: "date", label: "Date", type: "date" },
    ],
    columns: ["from_account", "to_account", "amount", "reason", "date"],
  },
  "acc-budgets": {
    title: "Budgets", subtitle: "Department & period budgets", endpoint: "/budgets",
    fields: [
      { name: "name", label: "Budget Name", required: true },
      { name: "department", label: "Department", ref: "/departments", refLabel: "name" },
      { name: "period", label: "Period", options: ["2026-Q1", "2026-Q2", "2026-Q3", "2026-Q4", "FY2026", "FY2027"] },
      { name: "amount", label: "Amount", type: "number" },
    ],
    columns: ["name", "department", "period", "amount"],
  },
  "acc-petty-cash": {
    title: "Petty Cash", subtitle: "Cash requests requiring approval", endpoint: "/petty-cash",
    approval_required: true,
    fields: [
      { name: "voucher_no", label: "Voucher No." },
      { name: "requester", label: "Requester", ref: "/employees", refLabel: "name" },
      { name: "amount", label: "Amount", type: "number", required: true },
      { name: "purpose", label: "Purpose", required: true },
    ],
    columns: ["voucher_no", "requester", "amount", "purpose", "status"],
    statusOptions: ["pending_approval", "approved", "rejected", "paid"],
  },

  // ============= Inventory =============
  "inv-categories": {
    title: "Product Categories", subtitle: "Classifications that drive each product's SKU prefix", endpoint: "/product-categories",
    fields: [
      { name: "name", label: "Category Name", required: true },
      { name: "code", label: "SKU Code (e.g. CCTV, NET)", required: true },
      { name: "description", label: "Description" },
    ],
    columns: ["name", "code", "description"],
  },
  "inv-office-items": {
    title: "Office Items", subtitle: "Furniture, stationery, appliances", endpoint: "/office-items",
    fields: [
      { name: "name", label: "Item Name", required: true },
      { name: "category", label: "Category", options: ["Furniture", "Stationery", "Appliances", "Kitchen", "Cleaning", "Decor"] },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit_price", label: "Unit Price", type: "number" },
      { name: "location", label: "Location", options: ["HQ Zanzibar", "Dar Branch", "Pemba Office", "Field Team"] },
      { name: "status", label: "Status", options: ["in-stock", "in-use", "consumed", "damaged"] },
    ],
    columns: ["name", "category", "quantity", "unit_price", "location", "status"],
    statusOptions: ["in-stock", "in-use", "consumed", "damaged"],
  },
  "inv-equipments": {
    title: "Equipments", subtitle: "Tools, test equipment, machinery", endpoint: "/equipments",
    fields: [
      { name: "name", label: "Equipment Name", required: true },
      { name: "category", label: "Category", options: ["Test Equipment", "Tools", "Machinery", "Safety", "Other"] },
      { name: "serial_no", label: "Serial No." },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit_price", label: "Unit Price", type: "number" },
      { name: "location", label: "Location", options: ["HQ Zanzibar", "Dar Branch", "Field Team", "Data Center", "Pemba Office"] },
      { name: "status", label: "Status", options: ["in-use", "idle", "maintenance", "disposed"] },
      { name: "warranty_until", label: "Warranty Until", type: "date" },
    ],
    columns: ["name", "category", "serial_no", "quantity", "location", "status", "warranty_until"],
    statusOptions: ["in-use", "idle", "maintenance", "disposed"],
  },
  "inv-movements": {
    title: "Stock Movements", subtitle: "Transfers, in/out records", endpoint: "/stock-movements",
    fields: [
      { name: "product", label: "Product", ref: "/products", refLabel: "name" },
      { name: "type", label: "Type", options: ["in", "out", "transfer", "adjustment"] },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "date", label: "Date", type: "date" },
    ],
    columns: ["product", "type", "quantity", "date"],
  },

  // ============= Assets =============
  "ast-register": {
    title: "Asset Register", subtitle: "All company assets", endpoint: "/assets",
    fields: [
      { name: "asset_no", label: "Asset No.", required: true },
      { name: "name", label: "Name", required: true },
      { name: "category", label: "Category", options: ["Vehicle", "IT Equipment", "Tools", "Building", "Furniture", "Other"] },
      { name: "value", label: "Value", type: "number" },
      { name: "location", label: "Location", options: ["HQ Zanzibar", "Dar Branch", "Pemba Office", "Field Team", "Data Center"] },
      { name: "status", label: "Status", options: STATUS_OPTIONS.asset },
      { name: "purchase_date", label: "Purchase Date", type: "date" },
    ],
    columns: ["asset_no", "name", "category", "value", "location", "status", "purchase_date"],
    statusOptions: STATUS_OPTIONS.asset,
  },
  "ast-maintenance": {
    title: "Asset Maintenance", subtitle: "Scheduled maintenance & service", endpoint: "/asset-maintenance",
    fields: [
      { name: "asset_no", label: "Asset", ref: "/assets", refLabel: "name", refValue: "asset_no" },
      { name: "type", label: "Type", options: ["routine", "repair", "inspection", "overhaul"] },
      { name: "date", label: "Date", type: "date" },
      { name: "cost", label: "Cost", type: "number" },
      { name: "status", label: "Status", options: ["scheduled", "in-progress", "completed"] },
    ],
    columns: ["asset_no", "type", "date", "cost", "status"],
  },

  // ============= HRM (no attendance/shifts) =============
  "hr-employees": {
    title: "Employees", subtitle: "Workforce directory", endpoint: "/employees",
    fields: [
      { name: "emp_no", label: "Emp No.", required: true },
      { name: "name", label: "Full Name", required: true },
      { name: "designation", label: "Designation", ref: "/designations", refLabel: "name" },
      { name: "department", label: "Department", ref: "/departments", refLabel: "name" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Phone" },
      { name: "salary", label: "Salary", type: "number" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.emp },
      { name: "join_date", label: "Join Date", type: "date" },
    ],
    columns: ["emp_no", "name", "designation", "department", "phone", "salary", "status"],
    statusOptions: STATUS_OPTIONS.emp,
  },
  "hr-departments": {
    title: "Departments", subtitle: "Organizational units", endpoint: "/departments",
    fields: [
      { name: "name", label: "Department Name", required: true },
      { name: "head", label: "Head", ref: "/employees", refLabel: "name" },
      { name: "headcount", label: "Headcount", type: "number" },
    ],
    columns: ["name", "head", "headcount"],
  },
  "hr-designations": {
    title: "Designations", subtitle: "Job titles and levels", endpoint: "/designations",
    fields: [
      { name: "name", label: "Title", required: true },
      { name: "level", label: "Level", options: ["L1", "L2", "L3", "L4", "L5", "Executive"] },
    ],
    columns: ["name", "level"],
  },
  "hr-leaves": {
    title: "Leave Requests", subtitle: "Employee leave (approval required)", endpoint: "/leaves",
    approval_required: true,
    fields: [
      { name: "emp_no", label: "Employee", ref: "/employees", refLabel: "name", refValue: "emp_no" },
      { name: "name", label: "Name" },
      { name: "leave_type", label: "Type", options: ["Annual", "Sick", "Maternity", "Paternity", "Unpaid", "Compassionate"] },
      { name: "from_date", label: "From", type: "date" },
      { name: "to_date", label: "To", type: "date" },
      { name: "days", label: "Days", type: "number" },
    ],
    columns: ["emp_no", "name", "leave_type", "from_date", "to_date", "days", "status"],
    statusOptions: ["pending_approval", "approved", "rejected"],
  },
  "hr-payroll": {
    title: "Payroll", subtitle: "Monthly payroll runs", endpoint: "/payroll",
    fields: [
      { name: "emp_no", label: "Employee", ref: "/employees", refLabel: "name", refValue: "emp_no" },
      { name: "name", label: "Name" },
      { name: "period", label: "Period", options: ["2026-01", "2026-02", "2026-03", "2026-04", "2026-05", "2026-06"] },
      { name: "gross", label: "Gross", type: "number" },
      { name: "tax", label: "Tax", type: "number" },
      { name: "net", label: "Net", type: "number" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.payroll },
    ],
    columns: ["emp_no", "name", "period", "gross", "tax", "net", "status"],
    statusOptions: STATUS_OPTIONS.payroll,
  },
  "hr-holidays": {
    title: "Holidays", subtitle: "Public & company holidays", endpoint: "/holidays",
    fields: [
      { name: "name", label: "Holiday", required: true },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "type", label: "Type", options: ["Public", "Company", "Religious"] },
    ],
    columns: ["name", "date", "type"],
  },

  // ============= Service =============
  "svc-tickets": {
    title: "Support Tickets", subtitle: "Customer support requests", endpoint: "/tickets",
    // "Create Task" on a ticket's detail view: a Daily Work task for a
    // technician, starting from the ticket (pages/dwr/Tasks.jsx).
    taskFromTicket: true,
    // Powers the "Create Work Order" button on a ticket's detail view --
    // prefills a new work order from this ticket instead of starting blank.
    workOrderPrefill: (record) => ({
      customer: record.customer || "",
      ticket: record.ticket_no || "",
      title: record.subject || "",
    }),
    // Same treatment as Work Order No.: shown pre-filled with the next
    // sequential number as soon as the New Ticket dialog opens, and never
    // hand-editable (see CrudPage.jsx's applyNumberPreview).
    previewNumberField: "ticket_no",
    fields: [
      { name: "ticket_no", label: "Ticket No.", readOnly: true },
      { name: "subject", label: "Subject", required: true },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "priority", label: "Priority", options: STATUS_OPTIONS.priority },
      { name: "status", label: "Status", options: STATUS_OPTIONS.ticket },
      { name: "assigned_to", label: "Assigned To", ref: "/employees", refLabel: "name" },
    ],
    columns: ["ticket_no", "subject", "customer", "priority", "status", "assigned_to"],
    statusOptions: STATUS_OPTIONS.ticket,
  },
  "svc-work-orders": {
    title: "Work Orders", subtitle: "Field & technician dispatch", endpoint: "/work-orders",
    // "Devices Installed" rows are entered here and synced server-side into
    // the standalone Device Registry (backend/device_sync.py) -- that's
    // what makes a serial number traceable system-wide, not just on this
    // one work order. customPrint renders the customer-facing completion
    // certificate (job done + devices + sign-off) instead of the generic
    // field dump.
    devices: true,
    customPrint: printWorkOrderCompletion,
    // Shown pre-filled with the next sequential number the moment the New
    // Work Order dialog opens (see CrudPage.jsx's openCreate/prefill
    // effects) -- a read-only preview from GET /work-orders/next-number,
    // not a reservation, so nothing is wasted if the dialog is cancelled.
    previewNumberField: "wo_no",
    fields: [
      { name: "wo_no", label: "WO No.", readOnly: true },
      { name: "title", label: "Title" },
      { name: "ticket", label: "Linked Ticket", ref: "/tickets", refLabel: "ticket_no", searchable: true },
      { name: "invoice_no", label: "Linked Invoice", ref: "/invoices", refLabel: "invoice_no", searchable: true },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "installation_site", label: "Installation Site" },
      { name: "technician", label: "Technician", ref: "/employees", refLabel: "name" },
      { name: "scheduled_date", label: "Scheduled", type: "date" },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "status", label: "Status", options: STATUS_OPTIONS.workorder },
      { name: "requirements", label: "Requirements", type: "textarea", placeholder: "What's needed for this job — equipment to buy, site access, parts…" },
      { name: "work_performed", label: "Work Performed", type: "textarea", placeholder: "What was actually done on site (fill in when completing the job)", showIf: (f) => f.status === "completed" || f.status === "in-progress" },
      { name: "completed_date", label: "Date Completed", type: "date", showIf: (f) => f.status === "completed" },
    ],
    columns: ["wo_no", "title", "customer", "technician", "scheduled_date", "due_date", "status"],
    statusOptions: STATUS_OPTIONS.workorder,
  },
  "svc-device-registry": {
    title: "Device Registry", subtitle: "Serial numbers & MAC addresses of every device installed", endpoint: "/device-installs",
    fields: [
      { name: "device_name", label: "Device / Item", required: true },
      { name: "serial_no", label: "Serial Number", required: true },
      { name: "mac_address", label: "MAC Address" },
      { name: "category", label: "Category", ref: "/product-categories", refLabel: "name" },
      { name: "customer", label: "Customer", ref: "/customers", refLabel: "name", searchable: true },
      { name: "wo_no", label: "Work Order", ref: "/work-orders", refLabel: "wo_no", searchable: true },
      { name: "installed_by", label: "Installed By", ref: "/employees", refLabel: "name" },
      { name: "installed_date", label: "Installed Date", type: "date" },
      { name: "status", label: "Status", options: ["installed", "replaced", "removed", "faulty"] },
      { name: "notes", label: "Notes" },
    ],
    columns: ["device_name", "serial_no", "mac_address", "customer", "wo_no", "status", "installed_date"],
    statusOptions: ["installed", "replaced", "removed", "faulty"],
  },
};
